import React, { useEffect, useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const PAYMENT_OPTIONS = [
  { id: "241", label: "PIX" },
  { id: "242", label: "Crédito" },
  { id: "267", label: "Boleto" },
];

// v1: sem "Online" (que depende do produto de emissao online). GFSIS: 2=video.
const VALIDATION_OPTIONS = [
  { id: "1", label: "Presencial" },
  { id: "2", label: "Videoconferência" },
];

const onlyDigits = value => String(value || "").replace(/\D/g, "");

const emptyForm = {
  document: "",
  personType: "pf",
  customerName: "",
  customerEmail: "",
  customerPhone: "",
  customerBirthDate: "",
  indicationDocument: "",
  cep: "",
  municipality: "",
  uf: "",
  neighborhood: "",
  number: "0",
  street: "",
  productCode: "",
  productName: "",
  certificateValue: "",
  paymentMethod: "241",
  validationType: "2",
  representativeName: "",
  representativeDocument: "",
  representativePhone: "",
  representativeEmail: "",
  representativeBirthDate: "",
};

const normalizeLabel = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getOrderRepresentative = order => {
  const rep = order?.representative || {};
  return {
    name: order?.representativeName || rep.name || rep.nome || "",
    document: onlyDigits(
      order?.representativeDocument || rep.document || rep.cpf || ""
    ),
    phone: order?.representativePhone || rep.phone || rep.telefone || "",
    email: order?.representativeEmail || rep.email || "",
    birthDate: order?.representativeBirthDate || rep.birthDate || "",
  };
};

const applyOrderToForm = (current, order, products = []) => {
  const document = onlyDigits(order?.document);
  const personType = document.length === 14 ? "pj" : "pf";
  const orderProductLabel = normalizeLabel(order?.productName);
  const matched =
    products.find(p => normalizeLabel(p.productName) === orderProductLabel) ||
    null;
  const rep = getOrderRepresentative(order);
  return {
    ...current,
    document: document || current.document,
    personType,
    customerName: order?.customerName || current.customerName,
    customerEmail: order?.email || current.customerEmail,
    customerPhone: order?.phone || current.customerPhone,
    productCode: matched?.productCode
      ? String(matched.productCode)
      : current.productCode,
    productName:
      matched?.productName || order?.productName || current.productName,
    certificateValue:
      order?.orderValue ||
      order?.productSuggestedValue ||
      current.certificateValue,
    indicationDocument:
      onlyDigits(order?.indicationRaw) || current.indicationDocument,
    representativeName: rep.name || current.representativeName,
    representativeDocument: rep.document || current.representativeDocument,
    representativePhone: rep.phone || current.representativePhone,
    representativeEmail: rep.email || current.representativeEmail,
    representativeBirthDate:
      rep.birthDate || current.representativeBirthDate,
  };
};

const useStyles = makeStyles(theme => ({
  submitWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
}));

const CertificateCreateOrderDialog = ({ open, onClose, contact, onCreated, order }) => {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, value) =>
    setForm(current => ({ ...current, [key]: value }));

  const isCompany = onlyDigits(form.document).length === 14;

  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyForm,
      customerName: contact?.name || "",
      customerPhone: contact?.number || "",
    });

    const load = async () => {
      setProductsLoading(true);
      let loadedProducts = [];
      try {
        const { data } = await api.get(
          "/certificates/lookups/create-order-products"
        );
        loadedProducts = data?.products || [];
        setProducts(loadedProducts);
      } catch (error) {
        toastError(error);
      }
      setProductsLoading(false);

      try {
        let sourceOrder = order || null;
        if (!sourceOrder && contact?.id) {
          const { data } = await api.get(
            `/contacts/${contact.id}/certificate-orders`,
            { params: { page: 1, limit: 1, all: true } }
          );
          sourceOrder = (data?.orders || [])[0] || null;
        }
        if (sourceOrder) {
          setForm(current =>
            applyOrderToForm(current, sourceOrder, loadedProducts)
          );
          // completa endereco/nascimento direto da base de clientes do GFSIS
          // (o pedido nao carrega endereco; sem isso o GFSIS recusa por tag)
          const digitsAuto = onlyDigits(sourceOrder.document);
          if ([11, 14].includes(digitsAuto.length)) {
            await fetchCustomerIntoForm(digitsAuto, { silent: true });
          }
        }
      } catch (error) {
        // prefill best-effort; nao bloqueia criacao manual
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchCustomerIntoForm = async (digits, { silent = false } = {}) => {
    try {
      const { data } = await api.get(
        "/certificates/lookups/customer-by-document",
        { params: { document: digits } }
      );
      const c = data?.customer || data || {};
      setForm(current => ({
        ...current,
        personType: c.personType || (digits.length === 14 ? "pj" : "pf"),
        customerName: c.name || current.customerName,
        customerEmail: c.email || current.customerEmail,
        customerPhone: c.phone || current.customerPhone,
        customerBirthDate: c.birthday || current.customerBirthDate,
        cep: c.address?.cep || current.cep,
        municipality: c.address?.municipio || current.municipality,
        uf: c.address?.uf || current.uf,
        neighborhood: c.address?.bairro || current.neighborhood,
        number: c.address?.numero || current.number,
        street: c.address?.logradouro || current.street,
        representativeName: c.representative?.name || current.representativeName,
        representativeDocument:
          c.representative?.document || current.representativeDocument,
        representativePhone:
          c.representative?.phone || current.representativePhone,
        representativeBirthDate:
          c.representative?.birthDate || current.representativeBirthDate,
      }));
      if (!silent) toast.success("Cliente carregado da base.");
      return true;
    } catch (error) {
      if (!silent) toastError(error);
      return false;
    }
  };

  const handleLookup = async () => {
    const digits = onlyDigits(form.document);
    if (![11, 14].includes(digits.length)) {
      toast.error("Informe um CPF ou CNPJ válido para buscar.");
      return;
    }
    setLookupLoading(true);
    await fetchCustomerIntoForm(digits);
    setLookupLoading(false);
  };

  const selectedProduct =
    products.find(p => String(p.productCode) === String(form.productCode)) ||
    null;

  const handleSubmit = async () => {
    const digits = onlyDigits(form.document);
    if (![11, 14].includes(digits.length)) {
      toast.error("Informe um CPF ou CNPJ válido.");
      return;
    }
    if (!form.customerName.trim()) {
      toast.error("Informe o nome do cliente.");
      return;
    }
    if (!form.productCode) {
      toast.error("Selecione um produto.");
      return;
    }
    if (!String(form.certificateValue).trim()) {
      toast.error("Informe o valor do certificado.");
      return;
    }
    if (isCompany && !onlyDigits(form.representativeDocument)) {
      toast.error("Informe o CPF do representante (pessoa jurídica).");
      return;
    }
    if (!onlyDigits(form.cep) || !form.municipality.trim() || !form.uf.trim()) {
      toast.error(
        "Endereço incompleto (CEP, município e UF): use a busca pelo CPF/CNPJ ou preencha manualmente."
      );
      return;
    }

    setSubmitting(true);
    try {
      const indicationDigits = onlyDigits(form.indicationDocument);
      const indicacao =
        indicationDigits.length === 11
          ? { cpf: indicationDigits }
          : indicationDigits.length >= 12
          ? { cnpj: indicationDigits }
          : undefined;
      const { data } = await api.post("/certificates/orders/create", {
        ...form,
        document: digits,
        personType: isCompany ? "pj" : "pf",
        ...(indicacao ? { indicacao } : {}),
      });
      const gfsis = data?.data || data || {};
      const codigo = gfsis.codigo || gfsis.id || "";
      const mensagem = gfsis.mensagem || gfsis.message || "Pedido criado!";
      toast.success(codigo ? `${mensagem} (Código ${codigo})` : mensagem);
      if (onCreated) onCreated();
      onClose();
    } catch (error) {
      toastError(error);
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Criar pedido</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <TextField
              label="CPF / CNPJ"
              fullWidth
              value={form.document}
              onChange={e => setField("document", e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              style={{ height: "100%" }}
              disabled={lookupLoading}
              onClick={handleLookup}
            >
              {lookupLoading ? <CircularProgress size={18} /> : "Buscar"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nome do cliente"
              fullWidth
              value={form.customerName}
              onChange={e => setField("customerName", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Email"
              fullWidth
              value={form.customerEmail}
              onChange={e => setField("customerEmail", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Telefone"
              fullWidth
              value={form.customerPhone}
              onChange={e => setField("customerPhone", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Indicação (CPF/CNPJ do parceiro)"
              fullWidth
              value={form.indicationDocument}
              onChange={e => setField("indicationDocument", e.target.value)}
            />
          </Grid>
          {!isCompany && (
            <Grid item xs={6}>
              <TextField
                label="Nascimento (DD/MM/AAAA)"
                fullWidth
                value={form.customerBirthDate}
                onChange={e => setField("customerBirthDate", e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Autocomplete
              options={products}
              loading={productsLoading}
              value={selectedProduct}
              getOptionLabel={option =>
                option ? `${option.productName} (${option.productCode})` : ""
              }
              getOptionSelected={(option, value) =>
                String(option.productCode) === String(value?.productCode)
              }
              onChange={(_e, option) => {
                setForm(current => ({
                  ...current,
                  productCode: option?.productCode
                    ? String(option.productCode)
                    : "",
                  productName: option?.productName || "",
                  certificateValue:
                    option?.suggestedValue || current.certificateValue || "",
                }));
              }}
              renderInput={params => (
                <TextField {...params} label="Produto" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Valor"
              fullWidth
              value={form.certificateValue}
              onChange={e => setField("certificateValue", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Forma de pagamento</FormLabel>
              <RadioGroup
                row
                value={form.paymentMethod}
                onChange={e => setField("paymentMethod", e.target.value)}
              >
                {PAYMENT_OPTIONS.map(o => (
                  <FormControlLabel
                    key={o.id}
                    value={o.id}
                    control={<Radio color="primary" />}
                    label={o.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Tipo de validação</FormLabel>
              <RadioGroup
                row
                value={form.validationType}
                onChange={e => setField("validationType", e.target.value)}
              >
                {VALIDATION_OPTIONS.map(o => (
                  <FormControlLabel
                    key={o.id}
                    value={o.id}
                    control={<Radio color="primary" />}
                    label={o.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Endereço
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="CEP"
              fullWidth
              value={form.cep}
              onChange={e => setField("cep", e.target.value)}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="Município"
              fullWidth
              value={form.municipality}
              onChange={e => setField("municipality", e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="UF"
              fullWidth
              value={form.uf}
              onChange={e => setField("uf", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Bairro"
              fullWidth
              value={form.neighborhood}
              onChange={e => setField("neighborhood", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Logradouro"
              fullWidth
              value={form.street}
              onChange={e => setField("street", e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="Número"
              fullWidth
              value={form.number}
              onChange={e => setField("number", e.target.value)}
            />
          </Grid>

          {isCompany && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Representante (PJ)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Nome do representante"
                  fullWidth
                  value={form.representativeName}
                  onChange={e => setField("representativeName", e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="CPF do representante"
                  fullWidth
                  value={form.representativeDocument}
                  onChange={e =>
                    setField("representativeDocument", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Telefone do representante"
                  fullWidth
                  value={form.representativePhone}
                  onChange={e => setField("representativePhone", e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Nascimento do representante"
                  fullWidth
                  value={form.representativeBirthDate}
                  onChange={e =>
                    setField("representativeBirthDate", e.target.value)
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="default">
          Cancelar
        </Button>
        <div className={classes.submitWrap}>
          {submitting && <CircularProgress size={20} />}
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Enviando pedido..." : "Criar pedido"}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateCreateOrderDialog;
